/**
 * Clase base abstracta para geocodificadores especializados vía WFS
 * 
 * Proporciona funcionalidad común para consultar servicios WFS de IECA, REDIAM, IAPH
 * y parsear respuestas GML/GeoJSON.
 * 
 * @module services/geocoding/specialized
 * @version 2.1.0 - Método fetchWFSFeatures centralizado (ARQ-02)
 * 
 * @changelog
 * - v2.1.0 (ARQ-02): Añadido fetchWFSFeatures() para centralizar peticiones HTTP
 * - v2.0.0 (TD-009): Integración de GeocodingErrors discriminated unions
 * - v1.1.0 (P3-04): Migración de FastFuzzy a FuzzySearchEngine para pre-indexación
 */

import type { AxiosInstance, AxiosError } from 'axios';
import axios from 'axios';
import { FuzzySearchEngine, type SearchableItem } from '../../search';
import type { GeocodingResult as LegacyGeocodingResult, SpecializedGeocoderConfig } from '../../../types/infrastructure';
import type { GeocodingResult as TypedResult } from '../../../types/errors';
import { 
  createErrorContext, 
  createError, 
  success, 
  failure,
} from '../../../types/errors';
import type { GeocodedCoordinates } from '../generic/CartoCiudadGeocoder';
import type {
  GeoJSONFeature,
  WFSResponse,
  GeoJSONFeatureCollection,
  GeoJSONGeometry,
  GeoJSONPointGeometry,
  GeoJSONMultiPointGeometry,
  GeoJSONPolygonGeometry,
  GeoJSONMultiPolygonGeometry,
  GeoJSONLineStringGeometry,
  GeoJSONMultiLineStringGeometry,
} from '../../../types/geojson';

/**
 * Feature GML parseada del WFS
 */
export interface WFSFeature {
  name: string;
  x: number;
  y: number;
  municipality?: string;
  province?: string;
  address?: string;
  properties: Record<string, unknown>;
}

/**
 * Opciones para búsqueda WFS
 */
export interface WFSSearchOptions {
  /** Nombre a buscar */
  name: string;
  
  /** Municipio para filtrar resultados */
  municipality?: string;
  
  /** Provincia para filtrar resultados */
  province?: string;
  
  /** Código INE del municipio (5 dígitos) */
  codMun?: string;
  
  /** BBOX para filtro espacial [minX, minY, maxX, maxY] */
  bbox?: [number, number, number, number];
  
  /** Límite de resultados */
  maxResults?: number;
}

/**
 * Clase base abstracta para geocodificadores especializados WFS
 * 
 * Subclases deben implementar:
 * - getDefaultConfig(): Configuración específica del servicio
 * - parseFeature(): Lógica de parseo específica del formato GML
 * - buildCQLFilter(): Construcción de filtros CQL específicos
 * 
 * @changelog v1.1.0: Usa FuzzySearchEngine con pre-indexación para búsquedas O(1)
 */
export abstract class WFSBaseGeocoder {
  protected config: SpecializedGeocoderConfig;
  protected axiosInstance: AxiosInstance;
  protected featureCache: Map<string, WFSFeature[]>;
  
  /** Motor de búsqueda fuzzy con pre-indexación (P3-04) */
  protected searchEngine: FuzzySearchEngine;
  
  /** Municipio actualmente indexado en searchEngine */
  protected currentIndexMunicipality: string | null = null;
  
  /** Features indexadas actualmente (para lookup por índice) */
  protected indexedFeatures: WFSFeature[] = [];

  constructor(config?: Partial<SpecializedGeocoderConfig>) {
    this.config = {
      ...this.getDefaultConfig(),
      ...config
    };

    this.axiosInstance = axios.create({
      timeout: this.config.timeout,
      headers: {
        'Accept': 'application/json, application/xml, text/xml',
      }
    });

    this.featureCache = new Map();
    
    // Inicializar motor de búsqueda fuzzy (P3-04)
    this.searchEngine = new FuzzySearchEngine({
      mode: 'fuzzy',
      limit: 5,
      threshold: 0.2, // Umbral bajo para no perder matches válidos
    });
  }

  /**
   * Configuración por defecto del servicio WFS específico
   * Debe ser implementado por cada geocodificador especializado
   */
  protected abstract getDefaultConfig(): SpecializedGeocoderConfig;

  /**
   * Parsea una feature GML/GeoJSON a formato interno
   * Debe ser implementado por cada geocodificador según estructura de datos
   */
  protected abstract parseFeature(feature: GeoJSONFeature): WFSFeature | null;

  /**
   * Convierte una geometría GeoJSON a tupla [x, y] UTM30.
   *
   * Acepta:
   * - Point: extrae `coordinates` directamente.
   * - MultiPoint con N=1: extrae `coordinates[0]` (formato real de los WFS IECA).
   * - Polygon: centroide naive del anillo exterior (DOP-WEMB-2=A).
   * - MultiPolygon con N=1: centroide naive del exterior del primer polígono.
   * - LineString: punto medio del array de vértices (DOP-WHLG-1=a).
   * - MultiLineString con N=1: punto medio de la primera línea (DOP-WHLG-2=B).
   * - MultiLineString con N>1: midpoint linear-referenced sobre unión de
   *   partes (DOP-WHLG-2=B, sub-WEMP). Fix HALLAZGO-B 15-may: corpus AAE
   *   `MIEA:LineasElect` 893 features 100 % MultiLineString, 66,5 %
   *   (594/893) con N>1 partes físicamente disconnected agrupadas bajo un
   *   único NOMBRE; la lógica previa (rechazo o descarte de [1..N-1])
   *   introducía sesgo p50 3 km / p95 16 km vs midpoint linear-referenced.
   *
   * Rechaza (devuelve `null`):
   * - MultiPoint con N>1 → log warn con `context` y `N` (DOP-WMPF-1=A).
   * - MultiPolygon con N>1 → log warn con `context` y `N` (DOP-WEMB-3=A).
   * - GeometryCollection u otras geometrías no contempladas.
   * - `geom` null/undefined o coordenadas mal formadas.
   *
   * Sub-0a → sub-WEMB → sub-WHUP → sub-WHLG → sub-WEMP: cierre
   * cross-geometría completo del cluster (Point + MultiPoint(N=1) +
   * Polygon + MultiPolygon(N=1) + LineString + MultiLineString(N≥1)).
   *
   * Justificación empírica del midpoint local en líneas: smoke REDIAM 13-may
   * sobre `ms:conducciones` (n=5, LineString puro 2-77 verts) y
   * `ms:colectores_saneamiento` (n=10, LineString puro 2-48 verts). Patrón
   * preexistente en `WFSEnergyGeocoder.parseFeature` para `MIEA:LineasElect`.
   *
   * @param geom Geometría GeoJSON a parsear (puede ser null o no soportada).
   * @param context Etiqueta diagnóstica opcional para logs (ej. nombre del geocoder + feature.id).
   * @returns Tupla `[x, y]` en EPSG:25830 o `null` si no se puede extraer un punto único.
   */
  protected parseGeometry(
    geom: GeoJSONGeometry | null | undefined,
    context?: string,
  ): [number, number] | null {
    if (!geom) return null;

    if (geom.type === 'Point') {
      const coords = (geom as GeoJSONPointGeometry).coordinates;
      if (!Array.isArray(coords) || coords.length < 2) return null;
      const x = coords[0];
      const y = coords[1];
      if (typeof x !== 'number' || typeof y !== 'number') return null;
      return [x, y];
    }

    if (geom.type === 'MultiPoint') {
      const coords = (geom as GeoJSONMultiPointGeometry).coordinates;
      if (!Array.isArray(coords) || coords.length === 0) return null;
      if (coords.length > 1) {
        // DOP-WMPF-1=A: rechazar MultiPoint multipunto para telemetría empírica.
        console.warn(
          `[WFSBase.parseGeometry] MultiPoint con ${coords.length} puntos descartado` +
            (context ? ` (${context})` : ''),
        );
        return null;
      }
      const first = coords[0];
      if (!Array.isArray(first) || first.length < 2) return null;
      const x = first[0];
      const y = first[1];
      if (typeof x !== 'number' || typeof y !== 'number') return null;
      return [x, y];
    }

    if (geom.type === 'Polygon') {
      const coords = (geom as GeoJSONPolygonGeometry).coordinates;
      if (!Array.isArray(coords) || coords.length === 0) return null;
      // Anillo exterior = coords[0]; anillos interiores = coords[1..n] (islas).
      // DOP-WEMB-2=A: centroide naive sobre exterior, interiores ignorados
      // (evidencia empírica 12-may CDAV cav_02_g03_06_Embalse: 0,16 % features
      // con interior, error <100 m sobre embalses de 5+ km irrelevante PTEL).
      return this.centroidPolygon(coords);
    }

    if (geom.type === 'MultiPolygon') {
      const coords = (geom as GeoJSONMultiPolygonGeometry).coordinates;
      if (!Array.isArray(coords) || coords.length === 0) return null;
      if (coords.length > 1) {
        // DOP-WEMB-3=A: rechazar MultiPolygon(N>1) con warn contextualizado
        // (coherencia con MultiPoint(N>1) del cluster sub-0a; ~0,8 % empírico).
        console.warn(
          `[WFSBase.parseGeometry] MultiPolygon con ${coords.length} polígonos descartado` +
            (context ? ` (${context})` : ''),
        );
        return null;
      }
      return this.centroidPolygon(coords[0]);
    }

    if (geom.type === 'LineString') {
      // DOP-WHLG-1=a: midpoint local (índice central del array de vértices).
      // Smoke REDIAM 13-may: ms:conducciones y ms:colectores_saneamiento
      // devuelven LineString puro 2-77 verts. El midpoint sobre array
      // es geométricamente impreciso para líneas en L o U pero coste
      // despreciable a escala UTM30 PTEL (precisión ±10 m irrelevante).
      const coords = (geom as GeoJSONLineStringGeometry).coordinates;
      return this.midpointLine(coords);
    }

    if (geom.type === 'MultiLineString') {
      const coords = (geom as GeoJSONMultiLineStringGeometry).coordinates;
      if (!Array.isArray(coords) || coords.length === 0) return null;
      if (coords.length === 1) {
        // DOP-WHLG-2=B (N=1): midpoint local de la primera línea (delega
        // a midpointLine para coherencia con caso LineString puro).
        return this.midpointLine(coords[0]);
      }
      // DOP-WHLG-2=B (N>1, sub-WEMP): midpoint linear-referenced sobre
      // unión de partes. Resuelve HALLAZGO-B (descarte silencioso partes
      // [1..N-1] introducía sesgo p50 3 km / p95 16 km vs unión).
      return this.midpointMultiLineUnion(coords, context);
    }

    // GeometryCollection u otras geometrías no contempladas → fuera de scope cluster sub-0a.
    return null;
  }

  /**
   * Calcula el centroide naive del anillo exterior de un Polygon GeoJSON.
   *
   * Recibe `rings` = array de anillos (LinearRing) en formato GeoJSON:
   * - `rings[0]` = anillo exterior (obligatorio).
   * - `rings[1..n]` = anillos interiores/islas (opcionales, ignorados aquí).
   *
   * DOP-WEMB-2=A: promedio aritmético de las coordenadas del exterior, sin
   * ponderación Shoelace ni resta de interiores. Justificación empírica
   * (smoke CDAV `cav_02_g03_06_Embalse` 12-may, n=611):
   * - 99,84 % features con 0 interiores (no requieren resta).
   * - 0,16 % (1 feature) con 1 interior — error <100 m sobre embalse 5+ km.
   * - Precisión UTM30 PTEL: ≥10 m irrelevante a escala de scoring.
   *
   * @param rings Anillos del Polygon (exterior + interiores opcionales).
   * @returns Centroide UTM30 o `null` si el exterior es degenerado.
   */
  private centroidPolygon(
    rings: Array<Array<[number, number]>>,
  ): [number, number] | null {
    const exterior = rings[0];
    if (!Array.isArray(exterior) || exterior.length === 0) return null;
    let sumX = 0;
    let sumY = 0;
    let n = 0;
    for (const c of exterior) {
      if (!Array.isArray(c) || c.length < 2) continue;
      const x = c[0];
      const y = c[1];
      if (typeof x !== 'number' || typeof y !== 'number') continue;
      sumX += x;
      sumY += y;
      n++;
    }
    if (n === 0) return null;
    return [sumX / n, sumY / n];
  }

  /**
   * Calcula el punto medio de una línea (LineString) GeoJSON.
   *
   * Devuelve el vértice central del array (índice `Math.floor(n/2)`):
   * - Líneas con `n=2` vértices → segundo punto (índice 1).
   * - Líneas con n par → vértice posterior al medio geométrico.
   * - Líneas con n impar → vértice central exacto.
   *
   * DOP-WHLG-1=a: midpoint sobre array es geométricamente impreciso para
   * polilíneas en L o U (no es el centroide por longitud), pero el coste
   * es despreciable a escala UTM30 PTEL (precisión requerida ≥ 10 m).
   * Patrón preexistente en `WFSEnergyGeocoder.parseFeature` (líneas
   * eléctricas `MIEA:LineasElect`), promovido aquí para cross-tipología.
   *
   * @param coords Array de vértices `[x, y]` (o `[x, y, z]`, z ignorada).
   * @returns Tupla `[x, y]` UTM30 o `null` si la línea es degenerada.
   */
  private midpointLine(
    coords: Array<[number, number] | [number, number, number]> | unknown,
  ): [number, number] | null {
    if (!Array.isArray(coords) || coords.length < 2) return null;
    const midIndex = Math.floor(coords.length / 2);
    const midpoint = coords[midIndex];
    if (!Array.isArray(midpoint) || midpoint.length < 2) return null;
    const x = midpoint[0];
    const y = midpoint[1];
    if (typeof x !== 'number' || typeof y !== 'number') return null;
    return [x, y];
  }

  /**
   * Calcula el midpoint linear-referenced sobre la unión de partes de un
   * MultiLineString con N>1 (DOP-WHLG-2=B, sub-WEMP).
   *
   * Algoritmo:
   * 1. Para cada parte, calcular su longitud euclidiana acumulada (suma de
   *    `sqrt(dx²+dy²)` entre vértices consecutivos en UTM30).
   * 2. Sumar las longitudes parciales → longitud total `L`.
   * 3. Localizar la parte `p` y el segmento `[v_i, v_{i+1}]` donde la
   *    longitud acumulada cruza `L/2`.
   * 4. Interpolar linealmente la fracción restante dentro del segmento.
   *
   * Justificación empírica (sub-WEMP 15-may, corpus AAE `MIEA:LineasElect`):
   * - 893 features 100 % MultiLineString, 66,5 % (594/893) con N>1.
   * - Partes [1..N-1] frecuentemente disconnected del primer tramo
   *   (85 % gap >100 m, p50 gap 3,4 km), agrupadas bajo un único NOMBRE
   *   (línea de transmisión partida administrativamente).
   * - Lógica previa `coords[0]` introducía sesgo p50 3 km / p95 16 km /
   *   max 50,8 km vs midpoint linear-referenced sobre unión.
   * - Post-fix esperado: p50 sesgo <10 m (umbral DOP-WHLG-1=a) sobre el
   *   corpus completo, verificado por script `verify.py` en `.ptel/p0_*`.
   *
   * Robustez:
   * - Si todas las partes son degeneradas (1 vértice o <2): devuelve el
   *   primer vértice válido encontrado (fallback no-null si existe).
   * - Si la longitud total es 0 (todos los vértices coincidentes): devuelve
   *   el primer vértice válido.
   * - Vértices mal formados (no array, longitud <2, no numéricos): se
   *   omiten silenciosamente del cómputo de longitud.
   *
   * @param coords Array de partes (cada parte = array de vértices `[x, y]`).
   * @param context Etiqueta diagnóstica opcional para logs.
   * @returns Tupla `[x, y]` UTM30 o `null` si no se puede extraer punto único.
   */
  private midpointMultiLineUnion(
    coords: Array<Array<[number, number] | [number, number, number]>> | unknown,
    context?: string,
  ): [number, number] | null {
    if (!Array.isArray(coords) || coords.length === 0) return null;

    // Paso 1: longitud por parte y total
    const partLengths: number[] = [];
    let totalLength = 0;
    for (const part of coords) {
      if (!Array.isArray(part) || part.length < 2) {
        partLengths.push(0);
        continue;
      }
      let len = 0;
      for (let i = 1; i < part.length; i++) {
        const a = part[i - 1];
        const b = part[i];
        if (!Array.isArray(a) || a.length < 2 || !Array.isArray(b) || b.length < 2) continue;
        const ax = a[0];
        const ay = a[1];
        const bx = b[0];
        const by = b[1];
        if (
          typeof ax !== 'number' ||
          typeof ay !== 'number' ||
          typeof bx !== 'number' ||
          typeof by !== 'number'
        ) {
          continue;
        }
        const dx = bx - ax;
        const dy = by - ay;
        len += Math.sqrt(dx * dx + dy * dy);
      }
      partLengths.push(len);
      totalLength += len;
    }

    // Fallback robustez: si todas las partes son degeneradas o longitud total 0
    if (totalLength === 0) {
      if (context) {
        console.warn(
          `[WFSBase.parseGeometry] MultiLineString con ${coords.length} partes degeneradas (L=0); ` +
            `fallback a primer vértice válido (${context})`,
        );
      }
      for (const part of coords) {
        if (!Array.isArray(part)) continue;
        for (const v of part) {
          if (Array.isArray(v) && v.length >= 2 && typeof v[0] === 'number' && typeof v[1] === 'number') {
            return [v[0], v[1]];
          }
        }
      }
      return null;
    }

    // Paso 2-4: localizar parte + segmento + interpolar
    const target = totalLength / 2;
    let cumulative = 0;
    for (let p = 0; p < coords.length; p++) {
      const part = coords[p];
      const partLen = partLengths[p];
      if (cumulative + partLen < target) {
        cumulative += partLen;
        continue;
      }
      if (!Array.isArray(part) || part.length < 2) {
        cumulative += partLen;
        continue;
      }
      // Midpoint cae dentro de esta parte
      const remaining = target - cumulative;
      let segCumulative = 0;
      for (let i = 1; i < part.length; i++) {
        const a = part[i - 1];
        const b = part[i];
        if (!Array.isArray(a) || a.length < 2 || !Array.isArray(b) || b.length < 2) continue;
        const ax = a[0];
        const ay = a[1];
        const bx = b[0];
        const by = b[1];
        if (
          typeof ax !== 'number' ||
          typeof ay !== 'number' ||
          typeof bx !== 'number' ||
          typeof by !== 'number'
        ) {
          continue;
        }
        const dx = bx - ax;
        const dy = by - ay;
        const segLen = Math.sqrt(dx * dx + dy * dy);
        if (segCumulative + segLen >= remaining) {
          const t = segLen > 0 ? (remaining - segCumulative) / segLen : 0;
          return [ax + t * dx, ay + t * dy];
        }
        segCumulative += segLen;
      }
      // Fallback (no debería ocurrir): último vértice válido de la parte
      for (let j = part.length - 1; j >= 0; j--) {
        const v = part[j];
        if (Array.isArray(v) && v.length >= 2 && typeof v[0] === 'number' && typeof v[1] === 'number') {
          return [v[0], v[1]];
        }
      }
      cumulative += partLen;
    }

    // Fallback final: último vértice válido del corpus
    for (let p = coords.length - 1; p >= 0; p--) {
      const part = coords[p];
      if (!Array.isArray(part)) continue;
      for (let j = part.length - 1; j >= 0; j--) {
        const v = part[j];
        if (Array.isArray(v) && v.length >= 2 && typeof v[0] === 'number' && typeof v[1] === 'number') {
          return [v[0], v[1]];
        }
      }
    }
    return null;
  }


  /**
   * Construye filtro CQL específico para el servicio
   * Puede ser sobrescrito para lógica personalizada
   */
  protected buildCQLFilter(options: WFSSearchOptions): string {
    const filters: string[] = [];

    // Filtro por municipio si está disponible
    if (options.municipality) {
      filters.push(`MUNICIPIO ILIKE '%${this.escapeCQL(options.municipality)}%'`);
    }

    // Filtro por provincia si está disponible
    if (options.province) {
      filters.push(`PROVINCIA ILIKE '%${this.escapeCQL(options.province)}%'`);
    }

    // Filtro BBOX si está disponible
    if (options.bbox) {
      const [minX, minY, maxX, maxY] = options.bbox;
      filters.push(`BBOX(the_geom,${minX},${minY},${maxX},${maxY},'EPSG:25830')`);
    }

    return filters.length > 0 ? filters.join(' AND ') : '';
  }

  /**
   * Escapa caracteres especiales para CQL
   */
  protected escapeCQL(text: string): string {
    return text.replace(/'/g, "''");
  }

  /**
   * Pre-indexa features de un municipio para búsquedas fuzzy rápidas
   * 
   * IMPORTANTE: Llamar UNA VEZ por municipio antes de múltiples búsquedas.
   * Si el municipio ya está indexado, no hace nada (evita re-indexación).
   * 
   * @param municipality - Nombre del municipio
   * @param features - Features WFS a indexar
   * @returns Número de features indexadas
   * 
   * @since v1.1.0 (P3-04)
   */
  protected indexMunicipalityFeatures(
    municipality: string,
    features: WFSFeature[]
  ): number {
    const normalizedMunicipality = municipality.toLowerCase().trim();
    
    // Si ya está indexado este municipio, no re-indexar
    if (this.currentIndexMunicipality === normalizedMunicipality) {
      return this.indexedFeatures.length;
    }
    
    // Guardar referencia a features para lookup posterior
    this.indexedFeatures = features;
    this.currentIndexMunicipality = normalizedMunicipality;
    
    // Convertir WFSFeature[] a SearchableItem[]
    const searchableItems: SearchableItem[] = features.map((feature, idx) => ({
      id: String(idx),
      text: feature.name || '',
      metadata: {
        x: feature.x,
        y: feature.y,
        municipality: feature.municipality,
        province: feature.province,
        address: feature.address,
        properties: feature.properties,
      },
    }));
    
    // Indexar en el motor de búsqueda
    return this.searchEngine.index(searchableItems);
  }

  /**
   * Geocodifica una infraestructura usando el servicio WFS especializado
   * 
   * @param options - Opciones de búsqueda (nombre, municipio, etc)
   * @returns Resultado tipado con coordenadas o error detallado
   */
  public async geocode(options: WFSSearchOptions): Promise<TypedResult<GeocodedCoordinates>> {
    const startTime = Date.now();
    
    // Determinar source según layerName para contexto de error
    const sourceMap: Record<string, 'wfs_dera' | 'wfs_sicess' | 'wfs_education' | 'wfs_iaph' | 'wfs_security'> = {
      'default': 'wfs_dera',
    };
    const source = sourceMap[this.config.layerName] || 'wfs_dera';
    
    // Contexto de error para trazabilidad
    const errorContext = createErrorContext(source, 'L1', {
      elementName: options.name,
      municipio: options.municipality,
      provincia: options.province,
    });
    
    try {
      // Construir URL de petición WFS GetFeature
      const params = this.buildWFSParams(options);
      
      // Realizar petición
      const response = await this.axiosInstance.get(this.config.wfsEndpoint, { params });

      // Parsear features
      const features = this.parseResponse(response.data);

      if (features.length === 0) {
        return failure(
          createError.noResults(
            errorContext,
            options.name,
            { municipality: options.municipality || '', province: options.province || '' },
            true
          )
        );
      }

      // Pre-indexar features del municipio (P3-04)
      if (options.municipality) {
        this.indexMunicipalityFeatures(options.municipality, features);
      }

      // Aplicar fuzzy matching con índice pre-construido
      const bestMatch = this.findBestMatch(options.name, features);

      if (!bestMatch || bestMatch.score < this.config.fuzzyThreshold) {
        return failure(
          createError.lowConfidence(
            errorContext,
            bestMatch?.score ? Math.round(bestMatch.score * 100) : 0,
            Math.round(this.config.fuzzyThreshold * 100),
            [{ factor: 'fuzzy_match', contribution: -(1 - (bestMatch?.score || 0)) * 100 }]
          )
        );
      }

      // Convertir a resultado de geocodificación
      const legacyResult = this.featureToGeocodingResult(bestMatch.feature, bestMatch.score, options.name);
      
      return success(this.legacyToTyped(legacyResult), {
        processingTime: Date.now() - startTime,
        sourcesConsulted: [source],
        finalSource: source,
        cascadeLevel: 'L1',
        fromCache: false,
      });

    } catch (error) {
      // Clasificar error de red
      const axiosError = error as AxiosError;
      
      if (axiosError.code === 'ECONNABORTED' || axiosError.message?.includes('timeout')) {
        return failure(
          createError.timeout(errorContext, this.config.wfsEndpoint, this.config.timeout)
        );
      }
      
      if (axiosError.response?.status === 429) {
        return failure(
          createError.rateLimit(errorContext, this.config.wfsEndpoint, 60)
        );
      }
      
      if (axiosError.response?.status && axiosError.response.status >= 500) {
        return failure(
          createError.serviceUnavailable(errorContext, this.config.wfsEndpoint, axiosError.response.status)
        );
      }
      
      // CORS o conexión
      if (axiosError.message?.includes('CORS') || axiosError.message?.includes('Network')) {
        return failure(
          createError.cors(errorContext, this.config.wfsEndpoint)
        );
      }
      
      return failure(
        createError.connection(errorContext, this.config.wfsEndpoint, 'unknown')
      );
    }
  }

  /**
   * Convierte resultado legacy a formato tipado
   */
  private legacyToTyped(legacy: LegacyGeocodingResult): GeocodedCoordinates {
    return {
      x: legacy.x,
      y: legacy.y,
      confidence: legacy.confidence,
      source: legacy.source,
      sourceLayer: legacy.sourceLayer,
      matchedName: legacy.matchedName,
      matchType: legacy.matchType,
      fuzzyScore: legacy.fuzzyScore,
      address: legacy.address,
      municipality: legacy.municipality,
      muniCode: legacy.muniCode,
      province: legacy.province,
      metadata: legacy.metadata,
    };
  }

  /**
   * Geocodifica con firma legacy (compatibilidad TD-009)
   * 
   * Wrapper que extrae datos de TypedResult para subclases que
   * aún usan la firma antigua `GeocodingResult | null`.
   * 
   * @param options - Opciones de búsqueda WFS
   * @returns GeocodingResult legacy o null si falla
   */
  public async geocodeLegacy(options: WFSSearchOptions): Promise<LegacyGeocodingResult | null> {
    const result = await this.geocode(options);
    if (result.success) {
      return result.data as unknown as LegacyGeocodingResult;
    }
    return null;
  }

  /**
   * Ejecuta una petición WFS y retorna las features parseadas.
   * Centraliza el manejo de errores HTTP para evitar duplicación en subclases.
   * 
   * @param params - Parámetros WFS construidos con buildWFSParams()
   * @param errorContext - Contexto de error creado con createErrorContext()
   * @returns Array de WFSFeature parseadas
   * @throws GeocodingError tipado según el tipo de fallo
   * 
   * @since v2.1.0 (ARQ-02)
   */
  protected async fetchWFSFeatures(
    params: Record<string, string>,
    errorContext: ReturnType<typeof createErrorContext>
  ): Promise<WFSFeature[]> {
    try {
      const response = await this.axiosInstance.get(this.config.wfsEndpoint, { params });
      return this.parseResponse(response.data);
    } catch (error) {
      const axiosError = error as AxiosError;
      
      if (axiosError.code === 'ECONNABORTED' || axiosError.message?.includes('timeout')) {
        throw createError.timeout(errorContext, this.config.wfsEndpoint, this.config.timeout);
      }
      
      if (axiosError.response?.status === 429) {
        throw createError.rateLimit(errorContext, this.config.wfsEndpoint, 60);
      }
      
      if (axiosError.response?.status && axiosError.response.status >= 500) {
        throw createError.serviceUnavailable(
          errorContext, 
          this.config.wfsEndpoint, 
          axiosError.response.status
        );
      }
      
      if (axiosError.message?.includes('CORS') || axiosError.message?.includes('Network')) {
        throw createError.cors(errorContext, this.config.wfsEndpoint);
      }
      
      throw createError.connection(errorContext, this.config.wfsEndpoint, 'unknown');
    }
  }

  /**
   * Construye parámetros para petición WFS GetFeature.
   *
   * **Default `outputFormat: 'application/json'`** (alias corto GeoServer).
   * Cubre los 3 endpoints WFS productivos basados en GeoServer estandar:
   * - AAE Energy (`agenciaandaluzadelaenergia.es/mapwms/wfs` — nombre
   *   engañoso, es GeoServer pese al path `/mapwms/`).
   * - CDAV (`ideandalucia.es/services/CDAV_02_hidrografia/wfs`).
   * - IECA geoserver (`institutodeestadisticaycartografia/geoserver-ieca/ows`).
   *
   * **Excepción cataloagada en `WFSHydraulicGeocoder.ts:300`** (override del
   * método): los endpoints REDIAM bajo `/medioambiente/mapwms/REDIAM_WFS_*`
   * son MapServer (no GeoServer) y solo aceptan el alias completo
   * `'application/json; subtype=geojson'`. Smoke 14-may sub-WUF confirma
   * la dicotomía mutuamente excluyente:
   * - GeoServer estandar → HTTP 400 con `outputformat: Failed to find
   *   response for output format application/json; subtype=geojson`.
   * - MapServer REDIAM → HTTP 400 con `msWFSGetFeature(): WFS server error.
   *   'application/json' is not a permitted output format for layer X`.
   *
   * **No promover el subtype al Base** (refutado empiricamente en sub-WUF
   * 14-may): la dicotomia es estructural por implementación del servidor,
   * no drift accidental. La línea L300 de `WFSHydraulicGeocoder` es la
   * excepción correcta, no la regla. Ref: bloque CERRADO sub-WUF §3.A.
   *
   * Param `count` migrado a WFS 2.0 canónico en sub-WMVC 14-may.
   */
  protected buildWFSParams(options: WFSSearchOptions): Record<string, string> {
    const params: Record<string, string> = {
      service: 'WFS',
      version: '2.0.0',
      request: 'GetFeature',
      typeName: this.config.layerName,
      outputFormat: 'application/json',
      srsName: this.config.outputSRS,
      count: String(options.maxResults || 100)  // WFS 2.0 canónico, migrado en sub-WMVC 14-may
    };

    // Agregar filtro CQL si existe
    const cqlFilter = this.buildCQLFilter(options);
    if (cqlFilter) {
      params['CQL_FILTER'] = cqlFilter;
    }

    return params;
  }

  /**
   * Parsea respuesta WFS (GeoJSON o GML)
   */
  protected parseResponse(data: WFSResponse): WFSFeature[] {
    const features: WFSFeature[] = [];

    // Detectar formato de respuesta
    const geoJson = data as GeoJSONFeatureCollection;
    if (geoJson?.type === 'FeatureCollection' && Array.isArray(geoJson.features)) {
      // GeoJSON
      for (const feature of geoJson.features) {
        const parsed = this.parseFeature(feature);
        if (parsed) {
          features.push(parsed);
        }
      }
    } else if (typeof data === 'string' && data.includes('gml:')) {
      // GML - requiere parsing XML
      // TODO: Implementar parser GML si es necesario
      console.warn('GML response detected but not implemented yet');
    }

    return features;
  }

  /**
   * Encuentra el mejor match usando FuzzySearchEngine pre-indexado
   * 
   * Estrategia (P3-04):
   * 1. Si hay índice activo → búsqueda O(1) con searchBest()
   * 2. Si no hay índice → indexar on-the-fly y buscar
   * 
   * @param searchName - Nombre a buscar
   * @param features - Features donde buscar (se indexan si no hay índice)
   * @returns Mejor match con score o null
   */
  protected findBestMatch(
    searchName: string, 
    features: WFSFeature[]
  ): { feature: WFSFeature; score: number } | null {
    
    if (features.length === 0) {
      return null;
    }

    // Verificar si necesitamos indexar
    if (!this.searchEngine.isReady || this.indexedFeatures !== features) {
      // Indexación on-the-fly (fallback si no se llamó indexMunicipalityFeatures)
      this.indexedFeatures = features;
      const searchableItems: SearchableItem[] = features.map((feature, idx) => ({
        id: String(idx),
        text: feature.name || '',
        metadata: { featureIndex: idx },
      }));
      this.searchEngine.index(searchableItems);
    }

    // Búsqueda fuzzy con índice pre-construido
    const result = this.searchEngine.searchBest(searchName, 0.2);

    if (!result) {
      return null;
    }

    // Recuperar feature original por índice
    const featureIndex = parseInt(result.item.id, 10);
    const feature = this.indexedFeatures[featureIndex];

    if (!feature) {
      return null;
    }

    return {
      feature,
      score: result.score,
    };
  }

  /**
   * Convierte WFSFeature a GeocodingResult
   */
  protected featureToGeocodingResult(
    feature: WFSFeature,
    fuzzyScore: number,
    _originalName: string
  ): LegacyGeocodingResult {
    return {
      x: feature.x,
      y: feature.y,
      confidence: Math.round(fuzzyScore * 100),
      source: this.config.layerName,
      matchedName: feature.name,
      fuzzyScore,
      address: feature.address,
      municipality: feature.municipality || '',
      province: feature.province || ''
    };
  }

  /**
   * Geocodifica múltiples infraestructuras en batch
   * 
   * Optimización P3-04: Pre-indexa features UNA VEZ para todas las búsquedas
   * del mismo municipio, logrando O(n) en lugar de O(n²).
   */
  public async geocodeBatch(
    searchOptions: WFSSearchOptions[]
  ): Promise<TypedResult<GeocodedCoordinates>[]> {
    const results = await Promise.allSettled(
      searchOptions.map(opts => this.geocode(opts))
    );

    return results.map(result => {
      if (result.status === 'fulfilled') {
        return result.value;
      }
      // Si Promise.allSettled rechaza (no debería con nuestro manejo interno)
      const errorContext = createErrorContext('wfs_dera', 'L1', {});
      return failure<GeocodedCoordinates>(
        createError.internal(errorContext, result.reason instanceof Error ? result.reason : undefined)
      );
    });
  }

  /**
   * Limpia caché de features y índice de búsqueda
   */
  public clearCache(): void {
    this.featureCache.clear();
    this.clearSearchIndex();
  }

  /**
   * Limpia solo el índice de búsqueda fuzzy
   * Útil para forzar re-indexación en siguiente búsqueda
   * 
   * @since v1.1.0 (P3-04)
   */
  public clearSearchIndex(): void {
    this.searchEngine.clear();
    this.currentIndexMunicipality = null;
    this.indexedFeatures = [];
  }

  /**
   * Obtiene estadísticas del geocodificador
   */
  public getStats(): { 
    cacheSize: number; 
    endpoint: string; 
    layer: string;
    searchIndexSize: number;
    currentIndexMunicipality: string | null;
  } {
    return {
      cacheSize: this.featureCache.size,
      endpoint: this.config.wfsEndpoint,
      layer: this.config.layerName,
      searchIndexSize: this.searchEngine.size,
      currentIndexMunicipality: this.currentIndexMunicipality,
    };
  }
}
